/**
 * Auth API client
 *
 * SECURITY: Authentication tokens are managed via httpOnly cookies.
 * All requests include credentials to send/receive cookies.
 * State-changing operations include CSRF tokens for protection.
 */

import { getCsrfHeaders } from "./csrf";
import { safeResponseJson } from "./safe-response-json";
import type { ApiResponse, LoginResponseData, UserResponseData } from "./types";

const DEFAULT_AUTH_PATH_PREFIX = "/auth";

function normalizeAuthPathPrefix(authPathPrefix: string): string {
  if (!authPathPrefix) {
    return DEFAULT_AUTH_PATH_PREFIX;
  }

  const trimmed = authPathPrefix.trim();
  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return withLeadingSlash.endsWith("/")
    ? withLeadingSlash.slice(0, -1)
    : withLeadingSlash;
}

function buildAuthUrl(
  baseUrl: string,
  authPathPrefix: string,
  endpoint: string,
): string {
  const normalizedBaseUrl = baseUrl.trim().replace(/\/+$/, "");
  const normalizedAuthPathPrefix = normalizeAuthPathPrefix(authPathPrefix);
  const path = `${normalizedAuthPathPrefix}/${endpoint}`;

  if (!normalizedBaseUrl) {
    return path;
  }

  return `${normalizedBaseUrl}${path}`;
}

/** Guest login - authenticate with a persistent guestId */
export async function guestLogin(
  baseUrl: string,
  guestId: string,
  guestDisplayName?: string,
  authPathPrefix = DEFAULT_AUTH_PATH_PREFIX,
): Promise<ApiResponse<LoginResponseData>> {
  const body: { guestId: string; guestDisplayName?: string } = { guestId };
  if (guestDisplayName !== undefined && guestDisplayName !== "") {
    body.guestDisplayName = guestDisplayName;
  }

  const response = await fetch(buildAuthUrl(baseUrl, authPathPrefix, "guest"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getCsrfHeaders(),
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  return safeResponseJson(response);
}

/** Register a new user. Server sets httpOnly cookies on success. */
export async function register(
  baseUrl: string,
  email: string,
  password: string,
  name?: string,
  authPathPrefix = DEFAULT_AUTH_PATH_PREFIX,
): Promise<ApiResponse<LoginResponseData>> {
  const body: Record<string, string> = { email, password };
  if (name) {
    body.name = name;
  }

  const response = await fetch(
    buildAuthUrl(baseUrl, authPathPrefix, "register"),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getCsrfHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(body),
    },
  );

  return safeResponseJson(response);
}

/** Login or register with Google OAuth ID token */
export async function googleLogin(
  baseUrl: string,
  idToken: string,
  authPathPrefix = DEFAULT_AUTH_PATH_PREFIX,
): Promise<ApiResponse<LoginResponseData>> {
  const response = await fetch(
    buildAuthUrl(baseUrl, authPathPrefix, "google"),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getCsrfHeaders(),
      },
      credentials: "include",
      body: JSON.stringify({ idToken }),
    },
  );

  return safeResponseJson(response);
}

/** Login with email and password */
export async function login(
  baseUrl: string,
  email: string,
  password: string,
  authPathPrefix = DEFAULT_AUTH_PATH_PREFIX,
): Promise<ApiResponse<LoginResponseData>> {
  const response = await fetch(buildAuthUrl(baseUrl, authPathPrefix, "login"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getCsrfHeaders(),
    },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });

  return safeResponseJson(response);
}

/** Logout current user. Server clears httpOnly cookies. */
export async function logout(
  baseUrl: string,
  authPathPrefix = DEFAULT_AUTH_PATH_PREFIX,
): Promise<ApiResponse<void>> {
  const response = await fetch(
    buildAuthUrl(baseUrl, authPathPrefix, "logout"),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getCsrfHeaders(),
      },
      credentials: "include",
    },
  );

  return safeResponseJson(response);
}

/** Verify current session is valid. Uses httpOnly cookie. */
export async function verifySession(
  baseUrl: string,
  authPathPrefix = DEFAULT_AUTH_PATH_PREFIX,
): Promise<ApiResponse<UserResponseData>> {
  const response = await fetch(buildAuthUrl(baseUrl, authPathPrefix, "me"), {
    method: "GET",
    credentials: "include",
  });

  return safeResponseJson(response);
}

/** Refresh access token. Server handles token refresh via httpOnly cookies. */
export async function refreshToken(
  baseUrl: string,
  authPathPrefix = DEFAULT_AUTH_PATH_PREFIX,
): Promise<ApiResponse<LoginResponseData>> {
  const response = await fetch(
    buildAuthUrl(baseUrl, authPathPrefix, "refresh"),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getCsrfHeaders(),
      },
      credentials: "include",
    },
  );

  return safeResponseJson(response);
}
